// feat_407 — tarjeta de una consulta en la Bandeja / Respondidas por la IA.
import React, { useState } from "react";
import { Box, Button, Chip, TextField, Typography, makeStyles } from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";
import ChecklistDialog from "./ChecklistDialog";
import {
  AUTHORSHIP_LABELS, Clamp, CopyId, REASON_LABELS, StatusChip, TYPE_LABELS,
  errorText, fmtConfidence, fmtDateTime,
} from "./shared";

const useStyles = makeStyles((theme) => ({
  card: {
    border: "1px solid #e3e8eb",
    borderLeft: "4px solid #90a4ae",
    borderRadius: 6,
    padding: theme.spacing(1.25, 1.5),
    marginBottom: theme.spacing(1),
    background: theme.palette.background.paper,
    cursor: "pointer",
    "&:hover": { boxShadow: "0 1px 4px rgba(0,0,0,.12)" },
  },
  late: { borderLeftColor: "#d32f2f", background: theme.palette.type === "dark" ? "#3b1f1f" : "#fff5f5" },
  border: { borderLeftColor: "#b71c1c" },
  head: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginBottom: 4 },
  chips: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 },
  label: { marginTop: 6, display: "block" },
  actions: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8, alignItems: "center" },
}));

/** Bien / Mal (con nota) / Corregir tema sobre UNA respuesta enviada. */
export const ReviewButtons = ({ answer, onDone }) => {
  const [bad, setBad] = useState(false);
  const [note, setNote] = useState("");
  const [topicOpen, setTopicOpen] = useState(false);
  const [topic, setTopic] = useState(answer.topic || "");
  const [busy, setBusy] = useState(false);
  const stop = (e) => e.stopPropagation();

  const review = async (verdict) => {
    if (verdict === "mal" && note.trim().length < 3) {
      toast.error("Contá en una nota qué estuvo mal.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/superadmin/support/messages/${answer.id}/review`, { verdict, note: verdict === "mal" ? note.trim() : undefined });
      toast.success(verdict === "bien" ? "Marcada como Bien (el banco la toma como aprobada)." : "Marcada como Mal (sale del banco).");
      setBad(false);
      onDone && onDone();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const saveTopic = async () => {
    setBusy(true);
    try {
      await api.post(`/superadmin/support/messages/${answer.id}/topic`, { topic });
      toast.success("Tema corregido.");
      setTopicOpen(false);
      onDone && onDone();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const v = answer.reviewStatus;
  return (
    <Box onClick={stop}>
      <Box style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
        <Button size="small" variant={v === "bien" ? "contained" : "outlined"} style={v === "bien" ? { background: "#2e7d32", color: "#fff" } : { color: "#2e7d32", borderColor: "#2e7d32" }} disabled={busy} onClick={() => review("bien")} data-review-good>
          ✓ Bien
        </Button>
        <Button size="small" variant={v === "mal" ? "contained" : "outlined"} style={v === "mal" ? { background: "#d32f2f", color: "#fff" } : { color: "#d32f2f", borderColor: "#d32f2f" }} disabled={busy} onClick={() => setBad((b) => !b)} data-review-bad>
          ✗ Mal
        </Button>
        <Button size="small" variant="outlined" disabled={busy} onClick={() => setTopicOpen((o) => !o)} data-review-topic>
          Corregir tema
        </Button>
        {v && (
          <Typography variant="caption" color="textSecondary">
            Revisada{answer.reviewedByName ? ` por ${answer.reviewedByName}` : ""} · {fmtDateTime(answer.reviewedAt)}
            {answer.reviewNote ? ` · “${answer.reviewNote}”` : ""}
          </Typography>
        )}
      </Box>
      {bad && (
        <Box style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
          <TextField size="small" variant="outlined" label="¿Qué estuvo mal? (obligatorio)" value={note} onChange={(e) => setNote(e.target.value)} style={{ flex: 1, minWidth: 220 }} inputProps={{ maxLength: 500 }} />
          <Button size="small" variant="contained" color="secondary" disabled={busy} onClick={() => review("mal")}>Guardar</Button>
        </Box>
      )}
      {topicOpen && (
        <Box style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
          <TextField size="small" variant="outlined" label="Tema" value={topic} onChange={(e) => setTopic(e.target.value)} style={{ flex: 1, minWidth: 200 }} inputProps={{ maxLength: 120 }} />
          <Button size="small" variant="contained" color="primary" disabled={busy || !topic.trim()} onClick={saveTopic}>Guardar</Button>
        </Box>
      )}
    </Box>
  );
};

const RequestCard = ({ r, onOpen, onChanged, canChecklist }) => {
  const classes = useStyles();
  const [checklistOpen, setChecklistOpen] = useState(false);
  const a = r.lastAnswer;
  const d = r.draft;
  const cls = [classes.card, r.status === "borde" ? classes.border : "", r.late ? classes.late : ""].join(" ");
  return (
    <div className={cls} onClick={() => onOpen(r.id)} data-support-card={r.id} data-late={r.late ? "1" : "0"}>
      <div className={classes.head}>
        <CopyId id={r.id} />
        <Chip size="small" variant="outlined" label={r.company ? `#${r.company.id} ${r.company.name}` : `#${r.companyId}`} />
        <Typography variant="caption" color="textSecondary">{fmtDateTime(r.createdAt)}</Typography>
        <StatusChip status={r.status} />
        {r.late && <Chip size="small" label="Más de 8 h" style={{ background: "#d32f2f", color: "#fff" }} />}
      </div>

      <Typography variant="caption" color="textSecondary" className={classes.label}>
        Pregunta{r.userName ? ` · ${r.userName}` : ""}{r.ticketId ? ` · ticket ${r.ticketId}` : ""}
      </Typography>
      <Clamp text={r.text} />

      {a ? (
        <>
          <Typography variant="caption" color="textSecondary" className={classes.label}>
            Lo que salió · {AUTHORSHIP_LABELS[a.authorship] || a.authorship}
            {a.userName ? ` · ${a.userName}` : ""}{a.actor ? ` (${a.actor})` : ""}
            {a.aiProvider ? ` · ${a.aiProvider}` : ""}{a.aiModel ? ` · ${a.aiModel}` : ""} · {fmtDateTime(a.createdAt)}
          </Typography>
          <Clamp text={a.body} />
        </>
      ) : d && d.answer && !["enviado", "descartado"].includes(d.status) ? (
        <>
          <Typography variant="caption" color="textSecondary" className={classes.label}>
            Borrador de la IA · {d.provider || "—"}{d.model ? ` · ${d.model}` : ""}
          </Typography>
          <Clamp text={d.answer} />
        </>
      ) : null}

      <div className={classes.chips}>
        <Chip size="small" variant="outlined" label={TYPE_LABELS[r.type] || r.type} />
        {(a?.topic || d?.topic) && <Chip size="small" variant="outlined" label={`Tema: ${a?.topic || d?.topic}`} />}
        {((a?.sources || d?.sources) || []).slice(0, 2).map((s, i) => (
          <Chip key={i} size="small" variant="outlined" label={`Fuente: ${s.archivo}${s.seccion ? ` § ${String(s.seccion).slice(0, 40)}` : ""}`} />
        ))}
        {(a?.confidence != null || d?.confidence != null) && (
          <Chip
            size="small"
            variant="outlined"
            label={`Confianza ${fmtConfidence(a?.confidence ?? d?.confidence)}`}
            style={(a?.confidence ?? d?.confidence) < 0.85 ? { borderColor: "#f57c00", color: "#e65100" } : {}}
          />
        )}
        {r.clarificationCount > 0 && <Chip size="small" label={`Repregunta ×${r.clarificationCount}`} style={{ background: "#8e24aa", color: "#fff" }} />}
        {r.checklistTaskId && <Chip size="small" label={`Checklist #${r.checklistTaskId}`} style={{ background: "#5d4037", color: "#fff" }} data-card-checklist />}
        {r.noManual && <Chip size="small" label="Sin manual" style={{ background: "#ff6f00", color: "#fff" }} data-no-manual />}
        {d?.border && <Chip size="small" label={`Borde${d.borderReason ? `: ${String(d.borderReason).split(":")[0]}` : ""}`} style={{ background: "#b71c1c", color: "#fff" }} />}
        {d?.decisionReason && !a && <Chip size="small" variant="outlined" label={REASON_LABELS[d.decisionReason] || d.decisionReason} />}
      </div>

      <div className={classes.actions}>
        <Button size="small" color="primary" variant="contained" onClick={(e) => { e.stopPropagation(); onOpen(r.id); }}>
          Abrir
        </Button>
        {canChecklist && r.status !== "cerrada" && (
          <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); setChecklistOpen(true); }} data-card-to-checklist={r.id}>
            {r.checklistTaskId ? "Ver checklist" : "Pasar a checklist"}
          </Button>
        )}
        {a && <ReviewButtons answer={a} onDone={onChanged} />}
      </div>
      {checklistOpen && (
        // El diálogo vive dentro de la tarjeta: cortar la propagación para que no abra el detalle.
        <div onClick={(e) => e.stopPropagation()}>
          <ChecklistDialog requestId={r.id} open onClose={() => setChecklistOpen(false)} onDone={onChanged} />
        </div>
      )}
    </div>
  );
};

export default RequestCard;
